import { ReactElement, useState } from "react";
import { Field, useFormState } from "react-final-form";
import ButtonContainer from "@components/ButtonContainer";
import CalendarMonthIcon from "mdi-react/CalendarMonthIcon";
import ViewHeadlineIcon from "mdi-react/ViewHeadlineIcon";
import FormTextField from "@components/Shared/FormTextField";
import { useTranslation } from "react-i18next";
import DatePickerField from "@components/Shared/FormDatePickerField";
import IFormProps from "@interfaces/Shared/formProps";
import renderSelectField from "@easydev/form/Select";
import useStates from "@mappers/Shared/getState.mapper";
import LinkVariantIcon from "mdi-react/LinkVariantIcon";
import { TIMELINE_CATEGORIES } from "@constants/Clients/rocketFactory/timeline";
import TwitterIcon from "mdi-react/TwitterIcon";
import InstagramIcon from "mdi-react/InstagramIcon";
import DiscordIcon from "mdi-react/DiscordIcon";
import { typeSocial } from "@constants/enums/shared/enum";
import Condition from "@components/EasyDev/form/Condition";
import { timelineCategory } from "@constants/Clients/rocketFactory/enum";
import useFormRules from "@hooks/Clients/RocketFactory/timeline/useFormRules";
import TextLongIcon from "mdi-react/TextLongIcon";
import FormatListBulletedTypeIcon from "mdi-react/FormatListBulletedTypeIcon";
import EyeOutlineIcon from "mdi-react/EyeOutlineIcon";
import FormTextareaField from "@components/Shared/FormTextareaField/FormTextareaField";

const CreateForm = ({ handleSubmit }: IFormProps): ReactElement => {
  const { t } = useTranslation(["rocketFactory", "core"]);
  const { GetState, GetStates } = useStates();
  const [status] = useState(GetState(true));
  const [date] = useState(new Date());
  const { values } = useFormState();
  const { showDescription, showLinkImage, showLink } = useFormRules(values);

  return (
    <form className="form form--horizontal" onSubmit={handleSubmit}>
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t("core:common.category")}
        </span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <FormatListBulletedTypeIcon />
          </div>
          <Field
            name="category"
            component={renderSelectField}
            type="text"
            placeholder={`${t("core:common.select_placeholder")} ${t(
              "core:common.category"
            )}`}
            options={TIMELINE_CATEGORIES}
            initialValue={TIMELINE_CATEGORIES[0]}
            isAboveError
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">{t("core:common.title")}</span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <ViewHeadlineIcon />
          </div>
          <Field
            name="title"
            component={FormTextField}
            type="text"
            placeholder="My Timeline Title"
            isAboveError
          />
        </div>
      </div>

      <div className="form__form-group">
        <span className="form__form-group-label">
          {t("core:common.publish_date")}
        </span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <CalendarMonthIcon />
          </div>
          <Field
            name="publishDate"
            component={DatePickerField}
            initialValue={date}
            type="text"
            isAboveError
          />
        </div>
      </div>

      <Condition when="category.value" is={timelineCategory.irlEvents}>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.closing_date")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <CalendarMonthIcon />
            </div>
            <Field
              name="closingDate"
              component={DatePickerField}
              type="text"
              isAboveError
            />
          </div>
        </div>
      </Condition>

      <Condition when="category.value" is={timelineCategory.press}>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.subtitle")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <ViewHeadlineIcon />
            </div>
            <Field
              name="subtitle"
              component={FormTextField}
              type="text"
              placeholder="My Timeline Subtitle"
              isAboveError
            />
          </div>
        </div>
      </Condition>

      {showLinkImage && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.image_link")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <LinkVariantIcon />
            </div>
            <Field
              name="linkImage"
              component={FormTextField}
              type="text"
              placeholder={`${t("core:common.add_an_placeholder")} ${t(
                "core:common.image_link"
              )}`}
              isAboveError
            />
          </div>
        </div>
      )}

      <Condition when="category.value" is={timelineCategory.movies}>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.video_link")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <LinkVariantIcon />
            </div>
            <Field
              name="linkVideo"
              component={FormTextField}
              type="text"
              placeholder={`${t("core:common.add_a_placeholder")} ${t(
                "core:common.video_link"
              )}`}
              isAboveError
            />
          </div>
        </div>
      </Condition>

      {showLink && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.external_link")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <LinkVariantIcon />
            </div>
            <Field
              name="link"
              component={FormTextField}
              type="text"
              placeholder={`${t("core:common.add_an_placeholder")} ${t(
                "core:common.external_link"
              )}`}
              isAboveError
            />
          </div>
        </div>
      )}

      {showDescription && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.description")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <TextLongIcon />
            </div>
            <Field
              name="body"
              component={FormTextareaField}
              placeholder="Type your description here."
              isAboveError
            />
          </div>
        </div>
      )}

      <Condition when="category.value" is={timelineCategory.irlEvents}>
        <div className="form__form-group">
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <InstagramIcon />
            </div>
            <Field
              name="instagram"
              component={FormTextField}
              type="text"
              placeholder={`${t("core:common.add_link_placeholder")} ${
                typeSocial.instagram
              }`}
              isAboveError
            />
          </div>
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">
            {t("core:common.socials")}
          </span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <TwitterIcon />
            </div>
            <Field
              name="twitter"
              component={FormTextField}
              type="text"
              placeholder={`${t("core:common.add_link_placeholder")} ${
                typeSocial.twitter
              }`}
              isAboveError
            />
          </div>
        </div>

        <div className="form__form-group">
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <DiscordIcon />
            </div>
            <Field
              name="discord"
              component={FormTextField}
              type="text"
              placeholder={`${t("core:common.add_link_placeholder")} ${
                typeSocial.discord
              }`}
              isAboveError
            />
          </div>
        </div>
      </Condition>

      <div className="form__form-group">
        <span className="form__form-group-label">{t("core:common.state")}</span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <EyeOutlineIcon />
          </div>
          <Field
            name="state"
            component={renderSelectField}
            type="text"
            placeholder={`${t("core:common.select_placeholder")} ${t(
              "core:common.state"
            )}`}
            options={GetStates()}
            initialValue={status}
          />
        </div>
      </div>

      <ButtonContainer />
    </form>
  );
};

export default CreateForm;
